import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, LineChart, Line, ComposedChart, Legend, 
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, FunnelChart
} from 'recharts';
import { 
  Briefcase, TrendingUp, Award, Filter, Clock, Download, Share2, RefreshCw, 
  ChartBar, Users, TrendingDown, DollarSign, Target, Building, CheckCircle,
  ChevronDown, ChevronUp, BookOpen, User, Binary, Network, 
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
// Enhanced color schemes
const CHART_COLORS = {
  primary: {
    purple: ['#7C3AED', '#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE'],
    green: ['#059669', '#10B981', '#34D399', '#6EE7B7', '#A7F3D0'],
    orange: ['#EA580C', '#F97316', '#FB923C', '#FDBA74', '#FED7AA'],
    blue: ['#2563EB', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE'],
    yellow: ['#CA8A04', '#EAB308', '#FACC15', '#FDE047', '#FEF08A']
  },
  status: {
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444'
  }
};

const CARD_SCHEMES = {
  purple: {
    background: 'bg-purple-50',
    icon: 'bg-purple-100 text-purple-600',
    accent: 'text-purple-600',
    border: 'border-purple-200',
  },
  green: {
    background: 'bg-green-50',
    icon: 'bg-green-100 text-green-600',
    accent: 'text-green-600',
    border: 'border-green-200',
  },
  orange: {
    background: 'bg-orange-50',
    icon: 'bg-orange-100 text-orange-600',
    accent: 'text-orange-600',
    border: 'border-orange-200',
  },
  blue: {
    background: 'bg-blue-50',
    icon: 'bg-blue-100 text-blue-600',
    accent: 'text-blue-600',
    border: 'border-blue-200',
  }
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: ChartBar },
  { id: 'job-types', label: 'Job Types', icon: Briefcase },
  { id: 'skills-analysis', label: 'Skills Analysis', icon: Binary }
];

// Utility Functions
const formatCurrency = (amount) => {
  if (!amount) return '0L';
  const inLacs = amount / 100000;
  return `${inLacs.toFixed(1)}L`;
};

const formatCurrencyRange = (min, max) => {
  if (!min || !max) return 'N/A';
  return `${formatCurrency(min)} - ${formatCurrency(max)}`;
};

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => currentYear - i);
};

// Reusable Components
const MetricCard = ({ title, value, icon: Icon, trend, subtext, scheme = "purple" }) => {
  const colors = CARD_SCHEMES[scheme];
  
  return (
    <div className={`${colors.background} rounded-2xl p-6 transition-all duration-300 
      hover:shadow-lg hover:scale-[1.02] relative overflow-hidden group border ${colors.border}`}
    >
      <div className="relative flex justify-between items-start">
        <div className={`p-3 rounded-xl ${colors.icon} transition-all duration-300 
          group-hover:scale-110`}>
          <Icon className="h-6 w-6" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center ${trend >= 0 ? 'text-green-600' : 'text-red-600'} 
            bg-white/80 px-2 py-1 rounded-lg`}>
            <span className="text-sm font-medium">
              {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
            </span>
            {trend >= 0 ? (
              <ChevronUp className="h-4 w-4 ml-1" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-1" />
            )}
          </div>
        )}
      </div>
      <div className="relative mt-4">
        <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        <p className="text-gray-600 text-sm mt-1 font-medium">{title}</p>
        {subtext && (
          <p className={`text-sm ${colors.accent} mt-2`}>{subtext}</p>
        )}
      </div>
    </div>
  );
};

const ChartCard = ({ title, subtitle, children, icon: Icon, scheme = "purple" }) => {
  const colors = CARD_SCHEMES[scheme];
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all border border-gray-100">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-lg ${colors.background}`}>
            <Icon className={`h-5 w-5 ${colors.accent}`} />
          </div>
        )}
      </div>
      {children}
    </div>
  );
};

const JobAnalytics = () => {
  const [data, setData] = useState(null);
  const [jobsData, setJobsData] = useState([]);
  const [alumniData, setAlumniData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('365');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [showExportMenu, setShowExportMenu] = useState(false);
  useEffect(() => {
    fetchData();
  }, [timeRange, selectedBatch]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [analyticsRes, jobsRes, alumniRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BASE_URL}/api/college/job-analytics?range=${timeRange}`, { headers }),
        fetch(`${process.env.REACT_APP_BASE_URL}/api/jobs`, { headers }),
        fetch(`${process.env.REACT_APP_BASE_URL}/api/college/alumni`, { headers })
      ]);

      if (!analyticsRes.ok || !jobsRes.ok || !alumniRes.ok) 
        throw new Error('Failed to fetch data');

      const [analyticsData, jobsList, alumniList] = await Promise.all([
        analyticsRes.json(),
        jobsRes.json(),
        alumniRes.json()
      ]);

      // Process data with batch filtering
      const processedJobs = processJobsData(jobsList, alumniList);
      
      const enhancedData = {
        ...analyticsData,
        recentJobs: processedJobs,
        jobsByYear: getYearWiseStats(processedJobs),
        applicationStats: getApplicationStats(processedJobs)
      };

      setData(enhancedData);
      setJobsData(processedJobs);
      setAlumniData(alumniList);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const processJobsData = (jobs, alumni) => {
    const filteredJobs = jobs.map(job => {
      let applications = job.applications.map(app => {
        const applicant = alumni.find(a => a._id === app.alumni);
        return {
          ...app,
          applicantInfo: applicant ? {
            name: `${applicant.firstName} ${applicant.lastName}`,
            graduationYear: applicant.graduationYear,
            department: applicant.department
          } : null
        };
      });

      // Filter applications based on selected batch
      if (selectedBatch !== 'all') {
        applications = applications.filter(app => 
          app.applicantInfo?.graduationYear === Number(selectedBatch)
        );
      }

      return {
        ...job,
        applications,
        processedData: {
          title: job.jobDetails.title,
          company: job.company.name,
          location: job.company.location,
          salary: {
            min: job.jobDetails.salary.min,
            max: job.jobDetails.salary.max,
            formatted: formatCurrencyRange(job.jobDetails.salary.min, job.jobDetails.salary.max)
          },
          type: job.jobDetails.type,
          skills: job.jobDetails.skills,
          experience: {
            min: job.jobDetails.experience.min,
            max: job.jobDetails.experience.max
          }
        }
      };
    });

    return filteredJobs;
  };

  const getYearWiseStats = (jobs) => {
    const stats = {};
    
    jobs.forEach(job => {
      job.applications.forEach(app => {
        if (!app.applicantInfo?.graduationYear) return;
        
        // Skip if doesn't match selected batch
        if (selectedBatch !== 'all' && 
            app.applicantInfo.graduationYear !== Number(selectedBatch)) return;

        const year = app.applicantInfo.graduationYear;
        if (!stats[year]) {
          stats[year] = {
            totalApplications: 0,
            uniqueJobs: new Set(),
            uniqueApplicants: new Set(),
            totalSalary: 0,
            skills: {},
            companies: new Set()
          };
        }
        stats[year].totalApplications++;
        stats[year].uniqueJobs.add(job._id);
        stats[year].uniqueApplicants.add(app.alumni);
        stats[year].totalSalary += (job.jobDetails.salary.min + job.jobDetails.salary.max) / 2;
        job.jobDetails.skills.forEach(skill => {
          stats[year].skills[skill] = (stats[year].skills[skill] || 0) + 1;
        });
        stats[year].companies.add(job.company.name);
      });
    });

    return Object.entries(stats).map(([year, data]) => ({
      year: parseInt(year),
      applications: data.totalApplications,
      uniqueJobs: data.uniqueJobs.size,
      uniqueApplicants: data.uniqueApplicants.size,
      averageSalary: data.totalSalary / data.uniqueJobs.size,
      companies: data.companies.size,
      topSkills: Object.entries(data.skills)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([skill, count]) => ({ skill, count }))
    }));
  };

  const getApplicationStats = (jobs) => {
    const stats = jobs.reduce((acc, job) => {
      const filteredApps = job.applications.filter(app => 
        selectedBatch === 'all' || 
        app.applicantInfo?.graduationYear === Number(selectedBatch)
      );

      return {
        totalApplications: acc.totalApplications + filteredApps.length,
        applicationsByType: {
          ...acc.applicationsByType,
          [job.jobDetails.type]: (acc.applicationsByType[job.jobDetails.type] || 0) + 
            filteredApps.length
        },
        applicationsBySkill: job.jobDetails.skills.reduce((skillAcc, skill) => ({
          ...skillAcc,
          [skill]: (skillAcc[skill] || 0) + filteredApps.length
        }), acc.applicationsBySkill)
      };
    }, {
      totalApplications: 0,
      applicationsByType: {},
      applicationsBySkill: {}
    });

    return stats;
  };

  const getFilteredMetrics = () => {
    if (!data || !jobsData) return null;

    const filteredJobs = jobsData.map(job => ({
      ...job,
      applications: job.applications.filter(app =>
        selectedBatch === 'all' ||
        app.applicantInfo?.graduationYear === Number(selectedBatch)
      )
    }));

    return {
      totalJobs: filteredJobs.length,
      totalApplications: filteredJobs.reduce((sum, job) => 
        sum + job.applications.length, 0),
      successfulApplications: filteredJobs.reduce((sum, job) =>
        sum + job.applications.filter(app => 
          app.status === 'accepted'
        ).length, 0),
      avgSalary: filteredJobs.reduce((sum, job) =>
        sum + (job.jobDetails.salary.min + job.jobDetails.salary.max) / 2, 0) / 
        (filteredJobs.length || 1),
      applicationsByType: filteredJobs.reduce((acc, job) => ({
        ...acc,
        [job.jobDetails.type]: (acc[job.jobDetails.type] || 0) + 
          job.applications.length
      }), {}),
      skillsDistribution: filteredJobs.reduce((acc, job) => {
        job.jobDetails.skills.forEach(skill => {
          acc[skill] = (acc[skill] || 0) + job.applications.length;
        });
        return acc;
      }, {})
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600" />
          <p className="text-purple-600 font-medium">Loading job analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <div className="bg-red-100 rounded-full p-3 w-12 h-12 mx-auto mb-4">
            <TrendingDown className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Analytics</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchData}
            className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 
              transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Get filtered metrics for the current view
  const metrics = getFilteredMetrics();
  const prepareExportData = () => {
    const metrics = getFilteredMetrics();
    
    return {
      overview: [
        ['Metric', 'Value'],
        ['Total Jobs', metrics.totalJobs],
        ['Total Applications', metrics.totalApplications],
        ['Success Rate', `${((metrics.successfulApplications / metrics.totalApplications) * 100).toFixed(1)}%`],
        ['Average Package', formatCurrency(metrics.avgSalary)],
      ],
      
      jobTypes: [
        ['Job Type', 'Applications', 'Percentage'],
        ...Object.entries(metrics.applicationsByType || {}).map(([type, count]) => [
          type,
          count,
          `${((count / metrics.totalApplications) * 100).toFixed(1)}%`
        ]),
      ],
      
      skills: [
        ['Skill', 'Demand Count', 'Percentage'],
        ...Object.entries(metrics.skillsDistribution || {})
          .sort(([, a], [, b]) => b - a)
          .map(([skill, count]) => [
            skill,
            count,
            `${((count / metrics.totalApplications) * 100).toFixed(1)}%`
          ])
      ],
      
      recentJobs: [
        ['Title', 'Company', 'Applications', 'Salary Range', 'Type'],
        ...jobsData.slice(0, 10).map(job => [
          job.processedData.title,
          job.processedData.company,
          job.applications.length,
          job.processedData.salary.formatted,
          job.processedData.type
        ])
      ]
    };
  };

  // Function to export to Excel
  const exportToExcel = () => {
    const data = prepareExportData();
    const wb = XLSX.utils.book_new();
    
    // Add each section as a worksheet
    Object.entries(data).forEach(([sheetName, sheetData]) => {
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(wb, ws, sheetName.charAt(0).toUpperCase() + sheetName.slice(1));
    });

    // Save the workbook
    XLSX.writeFile(wb, `job_analytics_${new Date().toLocaleDateString()}.xlsx`);
  };

  // Function to export to PDF
  const exportToPDF = () => {
    const data = prepareExportData();
    const doc = new jsPDF();
    
    // Set initial position
    let yPos = 20;
    
    // Add title
    doc.setFontSize(16);
    doc.text('Job Analytics Report', 20, yPos);
    yPos += 10;
    
    // Add date and filters
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, yPos);
    doc.text(`Time Range: ${timeRange} days`, 20, yPos + 5);
    doc.text(`Selected Batch: ${selectedBatch}`, 20, yPos + 10);
    yPos += 25;
    
    // Add sections
    Object.entries(data).forEach(([section, rows]) => {
      // Add section header
      doc.setFontSize(14);
      doc.setTextColor(82, 82, 255);
      doc.text(section.charAt(0).toUpperCase() + section.slice(1), 20, yPos);
      yPos += 10;
      
      // Reset text color
      doc.setTextColor(0);
      doc.setFontSize(10);
      
      // Add table data
      rows.forEach((row) => {
        // Check if we need a new page
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.text(row.join(' | '), 30, yPos);
        yPos += 7;
      });
      
      yPos += 10;
    });
    
    // Save the PDF
    doc.save(`job_analytics_${new Date().toLocaleDateString()}.pdf`);
  };

  const exportButton = (
    <div className="relative">
      <button
        onClick={() => setShowExportMenu(!showExportMenu)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 
          hover:bg-white/20 transition-colors border border-purple-400/30 
          text-white group"
      >
        <Download className="h-5 w-5 group-hover:scale-110 transition-transform" />
        <span>Export</span>
        <ChevronDown className="h-4 w-4" />
      </button>

      {showExportMenu && (
        <div className="absolute right-0 mt-2 w-48 rounded-lg bg-white shadow-lg 
          ring-1 ring-black ring-opacity-5 z-50"
          onBlur={() => setShowExportMenu(false)}
        >
          <div className="py-1">
            <button
              onClick={() => {
                exportToExcel();
                setShowExportMenu(false);
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 
                hover:bg-gray-100 w-full text-left"
            >
              <FileSpreadsheet className="h-4 w-4 text-green-600" />
              Export as Excel
            </button>
            <button
              onClick={() => {
                exportToPDF();
                setShowExportMenu(false);
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 
                hover:bg-gray-100 w-full text-left"
            >
              <FileText className="h-4 w-4 text-blue-600" />
              Export as PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 pb-32 pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-white">Job Analytics</h1>
              <p className="mt-2 text-purple-100">
                Comprehensive insights into job postings and applications
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-4">
              {/* Filters */}
              <div className="flex gap-4">
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="appearance-none bg-white/10 text-white border border-purple-400/30 
                    rounded-lg pl-4 pr-10 py-2 focus:ring-2 focus:ring-white/20 focus:outline-none"
                >
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 3 months</option>
                  <option value="180">Last 6 months</option>
                  <option value="365">Last year</option>
                  <option value="all">All time</option>
                </select>

                <select
                  value={selectedBatch}
                  onChange={(e) => setSelectedBatch(e.target.value)}
                  className="appearance-none bg-white/10 text-white border border-purple-400/30 
                    rounded-lg pl-4 pr-10 py-2 focus:ring-2 focus:ring-white/20 focus:outline-none"
                >
                  <option value="all">All Batches</option>
                  {getYearOptions().map(year => (
                    <option key={year} value={year}>{year} Batch</option>
                  ))}
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
      {exportButton}
     
      <button 
        onClick={fetchData}
        className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors
          border border-purple-400/30 text-white group"
      >
        <RefreshCw className="h-5 w-5 group-hover:scale-110 transition-transform" />
      </button>
    </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-12 border-b border-purple-400/30">
            <div className="flex gap-8 overflow-x-auto pb-px">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 pb-4 text-sm font-medium whitespace-nowrap
                    border-b-2 transition-all duration-300
                    ${activeTab === tab.id
                      ? 'text-white border-white'
                      : 'text-purple-100 border-transparent hover:text-white hover:border-white/50'
                    }
                  `}
                >
                  <tab.icon className={`h-4 w-4 ${activeTab === tab.id ? 'text-white' : 'text-purple-100'}`} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="-mt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Overview Tab Content */}
          {activeTab === 'overview' && (
            <>
              {/* Overview Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                  title="Total Jobs"
                  value={metrics?.totalJobs || 0}
                  icon={Briefcase}
                  subtext="Active job postings"
                  scheme="purple"
                />
                <MetricCard
                  title="Referral Applications"
                  value={metrics?.totalApplications || 0}
                  icon={Users}
                  subtext={`Applications ${selectedBatch !== 'all' ? `(Batch ${selectedBatch})` : ''}`}
                  scheme="green"
                />
               
                  <MetricCard
                    title="Avg Package"
                    value={formatCurrency(metrics?.avgSalary || 0)}
                    icon={DollarSign}
                    subtext="Average CTC"
                    scheme="blue"
                  />
                </div>
  
                {/* Chart Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                  {/* Job Types Distribution */}
                  <ChartCard 
                    title="Job Types Distribution" 
                    subtitle="Distribution by employment type"
                    icon={Briefcase}
                    scheme="purple"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={Object.entries(metrics?.applicationsByType || {})
                              .map(([type, count]) => ({
                                name: type,
                                value: count
                              }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {Object.entries(metrics?.applicationsByType || {}).map((_, index) => (
                              <Cell 
                                key={`cell-${index}`}
                                fill={CHART_COLORS.primary[
                                  Object.keys(CHART_COLORS.primary)[index % 
                                    Object.keys(CHART_COLORS.primary).length]
                                ][0]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>
  
                  {/* Skills Distribution */}
                  <ChartCard 
                    title="Top Skills in Demand" 
                    subtitle="Most requested skills"
                    icon={Binary}
                    scheme="green"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={Object.entries(metrics?.skillsDistribution || {})
                            .sort(([, a], [, b]) => b - a)
                            .slice(0, 8)
                            .map(([skill, count]) => ({
                              name: skill,
                              value: count
                            }))}
                          layout="vertical"
                          margin={{ left: 100 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" />
                          <Tooltip />
                          <Bar dataKey="value" fill={CHART_COLORS.primary.green[0]}>
                            {Object.entries(metrics?.skillsDistribution || {})
                              .slice(0, 8)
                              .map((_, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={CHART_COLORS.primary.green[index % 
                                    CHART_COLORS.primary.green.length]}
                                />
                              ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>
                </div>
  
                {/* Recent Jobs */}
                <div className="mt-6">
                  <ChartCard 
                    title="Recent Jobs" 
                    subtitle="Latest application activity"
                    icon={Briefcase}
                    scheme="blue"
                  >
                    <div className="space-y-4">
                      {jobsData
                        ?.slice(0, 5)
                        .map((job, index) => (
                          <div 
                            key={job._id}
                            className="flex items-center justify-between p-4 bg-gray-50 rounded-xl
                              hover:bg-gray-100 transition-all"
                          >
                            <div className="flex items-center gap-4">
                              <div className={`p-3 rounded-lg ${
                                CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].background
                              }`}>
                                <Briefcase className={`h-5 w-5 ${
                                  CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].accent
                                }`} />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {job.processedData.title}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {job.processedData.company}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900">
                                {job.applications.length} applications
                              </p>
                              <p className="text-sm text-gray-500">
                                {job.processedData.salary.formatted}
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </ChartCard>
                </div>
              </>
            )}
  
            {/* Job Types Tab */}
            {activeTab === 'job-types' && (
              <div className="space-y-6">
                {/* Type Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <MetricCard
                    title="Job Categories"
                    value={Object.keys(metrics?.applicationsByType || {}).length}
                    icon={Briefcase}
                    subtext="Different types"
                    scheme="purple"
                  />
                  <MetricCard
                    title="Most Popular"
                    value={Object.entries(metrics?.applicationsByType || {})
                      .sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A'}
                    icon={Target}
                    subtext="By applications"
                    scheme="green"
                  />
                  <MetricCard
                    title="Avg Applications"
                    value={(metrics?.totalApplications / 
                      (Object.keys(metrics?.applicationsByType || {}).length || 1)).toFixed(1)}
                    icon={Users}
                    subtext="Per job type"
                    scheme="orange"
                  />
                </div>
  
                {/* Type Analysis */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Type Distribution */}
                  <ChartCard 
                    title="Job Types Distribution" 
                    subtitle="Applications by job type"
                    icon={Briefcase}
                    scheme="purple"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={Object.entries(metrics?.applicationsByType || {})
                              .map(([type, count]) => ({
                                name: type,
                                value: count
                              }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {Object.entries(metrics?.applicationsByType || {})
                              .map((_, index) => (
                                <Cell 
                                  key={`cell-${index}`}
                                  fill={CHART_COLORS.primary[
                                    Object.keys(CHART_COLORS.primary)[index % 
                                      Object.keys(CHART_COLORS.primary).length]
                                  ][0]}
                                />
                              ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>
  
                  {/* Type Trends */}
                  <ChartCard 
                    title="Application Trends (Referral)" 
                    subtitle="Applications over time by type"
                    icon={TrendingUp}
                    scheme="green"
                  >
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={data?.jobsByYear || []}
                          margin={{ left: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="year" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="applications" 
                            stroke={CHART_COLORS.primary.green[0]} 
                            name="Applications"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </ChartCard>
                </div>
  
                {/* Type Details */}
                <ChartCard 
                  title="Job Type Details" 
                  subtitle="Detailed analysis by type"
                  icon={Briefcase}
                  scheme="orange"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(metrics?.applicationsByType || {})
                      .sort(([, a], [, b]) => b - a)
                      .map(([type, count], index) => {
                        const typeJobs = jobsData.filter(job => 
                          job.processedData.type === type
                        );
                        const avgSalary = typeJobs.reduce((sum, job) =>
                          sum + (job.processedData.salary.min + job.processedData.salary.max) / 2, 0
                        ) / (typeJobs.length || 1);
  
                        return (
                          <div
                            key={type}
                            className={`p-4 rounded-xl ${
                              CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].background
                            } border ${CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].border}`}
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <div className={`p-2 rounded-lg ${
                                CARD_SCHEMES[Object.keys(CARD_SCHEMES)[index % 4]].icon
                              }`}>
                                <Briefcase className="h-4 w-4" />
                              </div>
                              <h4 className="font-medium text-gray-900 capitalize">
                                {type.replace('-', ' ')}
                              </h4>
                            </div>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Applications</span>
                                <span className="font-medium text-gray-900">{count}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Avg. Salary</span>
                                <span className="font-medium text-gray-900">
                                  {formatCurrency(avgSalary)}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Success Rate</span>
                                <span className="font-medium text-gray-900">
                                  {((typeJobs.reduce((sum, job) =>
                                    sum + job.applications.filter(app => 
                                      app.status === 'accepted'
                                    ).length, 0) / count) * 100).toFixed(1)}%
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </ChartCard>
              </div>
            )}
  
           
              {/* Skills Analysis Tab */}
              {activeTab === 'skills-analysis' && (
                <div className="space-y-6">
                  {/* Skills Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <MetricCard
                      title="Total Skills"
                      value={Object.keys(metrics?.skillsDistribution || {}).length}
                      icon={Binary}
                      subtext="Unique skills"
                      scheme="purple"
                    />
                    <MetricCard
                      title="Top Skill"
                      value={Object.entries(metrics?.skillsDistribution || {})
                        .sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A'}
                      icon={Award}
                      subtext="Most in demand"
                      scheme="green"
                    />
                    <MetricCard
                      title="Avg Skills/Job"
                      value={(Object.values(metrics?.skillsDistribution || {})
                        .reduce((a, b) => a + b, 0) / 
                        (metrics?.totalJobs || 1)).toFixed(1)}
                      icon={Target}
                      subtext="Skills per posting"
                      scheme="orange"
                    />
                  </div>

                  {/* Skills Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Skills Distribution */}
                    <ChartCard 
                      title="Skills Distribution" 
                      subtitle="Most demanded skills"
                      icon={Binary}
                      scheme="purple"
                    >
                      <div className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={Object.entries(metrics?.skillsDistribution || {})
                              .sort(([, a], [, b]) => b - a)
                              .slice(0, 10)
                              .map(([skill, count]) => ({
                                name: skill,
                                value: count
                              }))}
                            layout="vertical"
                            margin={{ left: 120 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" width={100} />
                            <Tooltip />
                            <Bar dataKey="value" name="Applications">
                              {Object.entries(metrics?.skillsDistribution || {})
                                .slice(0, 10)
                                .map((_, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={CHART_COLORS.primary.purple[index % 
                                      CHART_COLORS.primary.purple.length]}
                                  />
                                ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </ChartCard>

                    {/* Skills Radar */}
                    <ChartCard 
                      title="Skills by Job Type" 
                      subtitle="Skills distribution across job types"
                      icon={Binary}
                      scheme="green"
                    >
                      <div className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart cx="50%" cy="50%" outerRadius="80%">
                            <PolarGrid />
                            <PolarAngleAxis 
                              dataKey="skill" 
                              tick={{ fill: '#4B5563' }}
                            />
                            <PolarRadiusAxis />
                            <Radar
                              name="Skills Distribution"
                              dataKey="value"
                              stroke={CHART_COLORS.primary.green[0]}
                              fill={CHART_COLORS.primary.green[0]}
                              fillOpacity={0.6}
                              data={Object.entries(metrics?.skillsDistribution || {})
                                .sort(([, a], [, b]) => b - a)
                                .slice(0, 8)
                                .map(([skill, value]) => ({
                                  skill,
                                  value
                                }))}
                            />
                            <Tooltip />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </ChartCard>
                  </div>

             
                </div>
              )}
            </div>
          
        </div>
      </div>
    );
};

export default JobAnalytics;
